<template>
    <div class="sidebar-panel">
        <kl-draw-zone-sidebar-header v-if="showSidebarTitle" :title="sidebarTitle" tag-style="h5"/>

        <vl-sidebar-content v-if="canAddKlbZone || canAddKlimZone" class="kl-sidebar__section"
                            ref="klb-actions">
            <vl-action-group class="kl-sidebar__section__actions">
                <vl-button v-if="canAddKlbZone" type="button" @click="addNewKlbZone" mod-small mod-block>
                    Nieuwe KLB-zone toevoegen
                </vl-button>
                <vl-button v-if="canAddKlimZone" type="button" @click="addNewKlimZone" mod-small mod-secondary mod-block>
                    Nieuwe KLIM-zone toevoegen
                </vl-button>
            </vl-action-group>
        </vl-sidebar-content>

        <vl-sidebar-content v-if="showKlbArchivedZoneToggle" class="kl-sidebar__section">
            <vl-checkbox :modelValue="showArchivedZonesAlso" @update:modelValue="toggleShowActive">Toon ook de gearchiveerde zones</vl-checkbox>
        </vl-sidebar-content>

        <vl-sidebar-content v-if="!hasZones" class="kl-sidebar__section">
            Er zijn geen zones gevonden.
        </vl-sidebar-content>

        <kl-draw-zone-sidebar-filler v-if="!hasZones"/>

        <vl-sidebar-content mod-container v-if="hasZones" class="kl-sidebar__section-no-border-top">
            <perfect-scrollbar
                :options="{ suppressScrollX: true, suppressScrollY: false, wheelPropagation: false }"
                class="kl-sidebar__scroll-area"
            >
                <ul class="kl-sidebar__list">
                    <li class="kl-sidebar__section" v-for="(cz) in notArchivedZones" :key="cz.id">
                        <kl-draw-zone-sidebar-klb-list-item
                            :zone="cz"
                            @highlight-klb-zone="onKlbZoneHover(cz.id)"
                            @edit-klb-zone="onEditKlbZone(cz.id)"
                        />
                    </li>
                </ul>
                <template v-if="hasArchivedZones">
                    <kl-draw-zone-sidebar-header title="Gearchiveerde zones" tag-style="h5"/>
                    <li class="kl-sidebar__section" v-for="(cz) in archivedZones" :key="cz.id">
                        <kl-draw-zone-sidebar-klb-list-item
                            :zone="cz"
                            @highlight-klb-zone="onKlbZoneHover(cz.id)"
                            @edit-klb-zone="onEditKlbZone(cz.id)"
                        />
                    </li>
                </template>
            </perfect-scrollbar>
        </vl-sidebar-content>
    </div>
</template>

<script lang="ts">

import {computed, defineComponent, onMounted, ref, watch} from 'vue';
import KlDrawZoneSidebarFiller
    from '@/app/shared/components/kl-draw-zone-map/components/kl-draw-zone-sidebar-filler.vue';
import KlDrawZoneSidebarHeader
    from '@/app/shared/components/kl-draw-zone-map/components/kl-draw-zone-sidebar-header.vue';
import {IDrawZone} from '@/app/shared/components/kl-draw-zone-map/components/kl-draw-zone-sidebar/kl-draw-zone-sidebar';
import {isEmpty} from 'lodash-es';
import KlDrawZoneSidebarKlbListItem
    from '@/app/shared/components/kl-draw-zone-map/components/kl-draw-zone-sidebar-klb-list-item.vue';

export default defineComponent({
    name: 'KlDrawZoneSidebarKlbList',
    emits: ['add-new-klb-zone', 'add-new-klim-zone', 'edit-klb-zone', 'highlight-klb-zone', 'toggle-show-active'],
    components: {
        KlDrawZoneSidebarFiller,
        KlDrawZoneSidebarHeader,
        KlDrawZoneSidebarKlbListItem,
    },
    props: {
        zones: {
            type: Array as () => IDrawZone[],
            default: () => [],
        },
        showSidebarTitle: { type: Boolean, default: true, },
        sidebarTitle: { type: String, default: null },
        canAddKlbZone: { type: Boolean, default: false },
        canAddKlimZone: {type: Boolean, default: false },
        // Toggle is only for UI, not for actual filtering.
        // Actual filtering is done in the parent component, because the toggle also triggers a different backend call.
        showKlbArchivedZoneToggle: { type: Boolean, default: false, },
        klbArchivedZoneToggleValue: { type: Boolean, default: false, },
    },
    setup(props, { emit }) {

        const showArchivedZonesAlso = ref(false);

        const notArchivedZones = computed((): IDrawZone[] => {
            return props.zones?.filter((zone: IDrawZone) => !zone.orgZoneInfo?.isArchived);
        });

        const archivedZones = computed((): IDrawZone[] => {
            return props.zones?.filter((zone: IDrawZone) => zone.orgZoneInfo?.isArchived);
        });

        const hasArchivedZones = computed(() => {
            return !isEmpty(archivedZones.value);
        });

        const hasZones = computed(() => {
            return !isEmpty(props.zones);
        });

        const onEditKlbZone = (zoneId: number) => {
            emit('edit-klb-zone', zoneId);
        }

        const onKlbZoneHover = (zoneId: number) => {
            emit('highlight-klb-zone', zoneId);
        }

        const addNewKlbZone = (zoneId: number) => {
            emit('add-new-klb-zone', zoneId);
        }

        const addNewKlimZone = (zoneId: number) => {
            emit('add-new-klim-zone', zoneId);
        }

        const toggleShowActive = (newValue: boolean) => {
            showArchivedZonesAlso.value = newValue;
            emit('toggle-show-active', newValue);
        }

        watch(
            () => props.klbArchivedZoneToggleValue,
            (newValue: boolean) => {
                showArchivedZonesAlso.value = props.klbArchivedZoneToggleValue
            },
            {immediate: true});

        return {
            showArchivedZonesAlso,
            hasZones,
            notArchivedZones,
            archivedZones,
            hasArchivedZones,
            onEditKlbZone,
            onKlbZoneHover,
            addNewKlbZone,
            addNewKlimZone,
            toggleShowActive,
        }
    }
})

</script>
